
import { useState } from 'react';

export default function Home() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const services = [
    {
      title: "Self-Employed",
      description: "Comprehensive health insurance solutions tailored for independent professionals and entrepreneurs",
      icon: "ri-user-line"
    },
    {
      title: "Traveling Nurse PPO Coverage",
      description: "Specialized PPO plans designed for healthcare professionals on the move",
      icon: "ri-nurse-line"
    },
    {
      title: "Small Business Owners",
      description: "Complete business insurance packages to protect your company and employees",
      icon: "ri-building-line"
    },
    {
      title: "Group Benefits at Competitive Rates",
      description: "Cost-effective group insurance solutions with excellent coverage options",
      icon: "ri-team-line"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Yellow Banner */}
      <div className="bg-yellow-400 text-black py-3 px-4 text-center font-semibold">
        <div className="flex items-center justify-center gap-2">
          <i className="ri-time-line text-lg"></i>
          <span>Open 24 Hours - Now Doing Online Appointments for Open Enrollment - Call 281-916-5196</span>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-blue-600" style={{ fontFamily: '"Pacifico", serif' }}>
                Sugar Land Health Insurance Agency
              </h1>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <div className="flex items-center text-gray-600">
                <i className="ri-map-pin-line mr-2"></i>
                <span className="text-sm">101 Southwestern Blvd #106, Sugar Land, TX 77478</span>
              </div>
              <div className="flex items-center text-blue-600 font-semibold">
                <i className="ri-phone-line mr-2"></i>
                <span>281-916-5196</span>
              </div>
            </div>

            <button 
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t">
              <div className="space-y-3">
                <div className="flex items-center text-gray-600">
                  <i className="ri-map-pin-line mr-2"></i>
                  <span className="text-sm">101 Southwestern Blvd #106, Sugar Land, TX 77478</span>
                </div>
                <div className="flex items-center text-blue-600 font-semibold">
                  <i className="ri-phone-line mr-2"></i>
                  <span>281-916-5196</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative py-20 px-4 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(37, 99, 235, 0.8), rgba(59, 130, 246, 0.8)), url('https://public.readdy.ai/ai/img_res/0e3910a449defd2a4c546f09a8ce9b6a.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto text-center text-white">
          <h2 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Your Insurance Success Story Starts Here
          </h2>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed">
            Celebrating every milestone with comprehensive coverage solutions tailored for your unique needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              className="bg-yellow-400 text-black px-8 py-4 rounded-full font-semibold text-lg hover:bg-yellow-300 transition-colors whitespace-nowrap cursor-pointer"
              onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
            >
              Schedule Online Appointment
            </button>
            <button className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
              Get Free Quote
            </button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">
              Specialized Coverage Solutions
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We celebrate your success by providing innovative insurance solutions for every stage of your journey
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-2xl font-bold text-gray-900 mb-3">
                      {service.title}
                    </h4>
                    <p className="text-gray-600 text-lg leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-4xl font-bold text-gray-900 mb-6">
                Why Celebrate with SL Insurance?
              </h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Expert Guidance</h4>
                    <p className="text-gray-600">Our experienced team provides personalized insurance solutions</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Competitive Rates</h4>
                    <p className="text-gray-600">We negotiate the best rates for comprehensive coverage</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Online Convenience</h4>
                    <p className="text-gray-600">Schedule appointments and manage policies online</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Happy%20insurance%20clients%20celebrating%20with%20insurance%20agent%20in%20modern%20office%2C%20diverse%20group%20of%20people%20smiling%20and%20shaking%20hands%2C%20professional%20business%20meeting%2C%20successful%20insurance%20consultation%2C%20bright%20office%20environment%2C%20celebration%20of%20insurance%20success%2C%20teamwork%20and%20satisfaction&width=600&height=400&seq=clients-celebration&orientation=landscape"
                alt="Happy clients celebrating insurance success"
                className="rounded-xl shadow-lg w-full h-96 object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h3 className="text-4xl font-bold mb-6">
            Ready to Celebrate Your Insurance Success?
          </h3>
          <p className="text-xl mb-8">
            Join thousands of satisfied clients who trust SL Insurance Agency for their coverage needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              className="bg-yellow-400 text-black px-8 py-4 rounded-full font-semibold text-lg hover:bg-yellow-300 transition-colors whitespace-nowrap cursor-pointer"
              onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
            >
              Book Online Appointment
            </button>
            <button className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
              Call 281-916-5196
            </button>
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">
              Celebrating Our Community
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join our celebration of success stories and community achievements
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="relative group overflow-hidden rounded-xl shadow-lg">
              <img 
                src="https://static.readdy.ai/image/3ce37778a322554914e024051b29af27/dfbdf8eee1d3ac04ab186f14a60a49df.png"
                alt="Community celebration with balloons"
                className="w-full h-64 object-cover object-top group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-4 left-4 text-white">
                  <h4 className="text-lg font-semibold">Community Success</h4>
                  <p className="text-sm">Celebrating achievements together</p>
                </div>
              </div>
            </div>

            <div className="relative group overflow-hidden rounded-xl shadow-lg">
              <img 
                src="https://readdy.ai/api/search-image?query=Happy%20insurance%20clients%20celebrating%20with%20insurance%20agent%20in%20modern%20office%2C%20diverse%20group%20of%20people%20smiling%20and%20holding%20colorful%20balloons%2C%20professional%20business%20meeting%2C%20successful%20insurance%20consultation%2C%20bright%20office%20environment%2C%20celebration%20of%20insurance%20success%2C%20teamwork%20and%20satisfaction%2C%20modern%20office%20with%20glass%20windows%20and%20professional%20atmosphere&width=400&height=300&seq=gallery1&orientation=landscape"
                alt="Happy clients celebrating insurance success"
                className="w-full h-64 object-cover object-top group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-4 left-4 text-white">
                  <h4 className="text-lg font-semibold">Client Success Stories</h4>
                  <p className="text-sm">Celebrating protected families</p>
                </div>
              </div>
            </div>

            <div className="relative group overflow-hidden rounded-xl shadow-lg">
              <img 
                src="https://readdy.ai/api/search-image?query=Small%20business%20owners%20celebrating%20insurance%20coverage%20success%2C%20entrepreneurs%20and%20business%20professionals%20in%20celebration%20with%20colorful%20balloons%2C%20modern%20business%20office%20environment%2C%20successful%20business%20insurance%20consultation%2C%20diverse%20group%20of%20happy%20business%20owners%2C%20professional%20celebration%20atmosphere&width=400&height=300&seq=gallery3&orientation=landscape"
                alt="Small business owners celebrating coverage"
                className="w-full h-64 object-cover object-top group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-4 left-4 text-white">
                  <h4 className="text-lg font-semibold">Business Protection</h4>
                  <p className="text-sm">Securing business futures</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h4 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Pacifico", serif' }}>
                Sugar Land Health Insurance Agency
              </h4>
              <p className="text-gray-300 mb-4">
                Your trusted partner for comprehensive insurance solutions in Sugar Land and beyond.
              </p>
              <p className="text-yellow-400 font-semibold">
                Health Insurance Agents for You
              </p>
            </div>
            
            <div>
              <h5 className="text-lg font-semibold mb-4">Our Services</h5>
              <ul className="space-y-2 text-gray-300">
                <li>Self-Employed Insurance</li>
                <li>Traveling Nurse PPO Coverage</li>
                <li>Small Business Insurance</li>
                <li>Group Benefits</li>
              </ul>
            </div>
            
            <div>
              <h5 className="text-lg font-semibold mb-4">Contact Info</h5>
              <div className="space-y-3 text-gray-300">
                <div className="flex items-start">
                  <i className="ri-map-pin-line mr-3 mt-1"></i>
                  <span>101 Southwestern Blvd #106<br />Sugar Land, TX 77478<br />United States</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-phone-line mr-3"></i>
                  <span>281-916-5196</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Sugar Land Health Insurance Agency. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
